import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CreditCard, Eye, Lock, Pencil, RefreshCw, Ban, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, fmtDate, fmtDateOnly, fmtMoney } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge, BillingBadge, MerchantBadge, Money, PageHeader } from "@/components/Primitives";
import { CardRevealModal } from "@/components/CardRevealModal";
import { ProcessChargeDialog } from "@/components/ProcessChargeDialog";
import { DocumentsPanel } from "@/components/DocumentsPanel";
import { Button } from "@/components/ui/button";
import { chargeTarget, BILLING } from "@/lib/constants";

function Row({ k, v, mono }) {
  return <div className="flex justify-between gap-4 py-1.5 text-sm border-b border-slate-800/60 last:border-0"><span className="text-slate-500">{k}</span><span className={`text-right ${mono ? "font-mono" : ""}`}>{v || "—"}</span></div>;
}

const ACTION_LABEL = {
  booking_submitted: "Submitted to Payment Desk", booking_updated: "Booking edited", booking_resubmitted: "Resubmitted to Payment Desk",
  card_revealed: "Card data revealed", payment_charged: "Charged", payment_partial: "Partial charge", payment_declined: "Declined",
  payment_failed: "Failed", booking_voided: "Voided by admin", document_uploaded: "Document uploaded", document_deleted: "Document deleted",
  agent_notified: "Agent emailed", agent_notify_failed: "Agent email failed",
};

export default function BookingDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [reveal, setReveal] = useState(false);
  const [proc, setProc] = useState(false);

  const load = useCallback(() => api.get(`/bookings/${id}`).then((r) => setData(r.data)).catch((e) => { toast.error(apiError(e)); nav("/bookings"); }), [id, nav]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="text-slate-500">Loading…</div>;
  const { booking: b, documents, audit } = data;
  const p = b.payment;
  const isAgent = user.role === "agent";
  const canEdit = (isAgent || user.role === "admin") && !b.locked;
  const canProcess = (user.role === "payment_team" || user.role === "admin") && b.charge_status === "pending";

  async function voidBooking() {
    const reason = window.prompt("Reason for voiding this booking:");
    if (!reason) return;
    try { await api.post(`/bookings/${b.id}/void`, { reason }); toast.success("Booking voided"); load(); }
    catch (e) { toast.error(apiError(e)); }
  }

  return (
    <div data-testid="booking-detail-page">
      <Link to={isAgent ? "/bookings" : user.role === "payment_team" ? "/payment-desk" : "/bookings"} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-200 mb-3"><ArrowLeft className="h-4 w-4" />Back</Link>
      <PageHeader eyebrow={`${b.product_category} · ${b.agent_name}`} title={<span className="font-mono" data-testid="detail-booking-ref">{b.booking_ref}</span>}>
        <StatusBadge status={b.charge_status} locked={b.locked} testId="detail-status-badge" />
        {canEdit && isAgent && <Button asChild variant="outline" className="border-slate-700"><Link to={`/bookings/${b.id}/edit`} data-testid="edit-booking-btn"><Pencil className="h-4 w-4 mr-2" />Edit</Link></Button>}
        {isAgent && ["declined", "failed"].includes(b.charge_status) && <Button asChild className="bg-amber-600 hover:bg-amber-500 text-slate-950"><Link to={`/bookings/${b.id}/edit?resubmit=1`} data-testid="resubmit-booking-btn"><RefreshCw className="h-4 w-4 mr-2" />Fix & resubmit</Link></Button>}
        {canProcess && <Button data-testid="process-charge-btn" onClick={() => setProc(true)} className="bg-indigo-600 hover:bg-indigo-500"><CreditCard className="h-4 w-4 mr-2" />Record charge outcome</Button>}
        {user.role === "admin" && !b.locked && <Button data-testid="void-booking-btn" variant="outline" className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10" onClick={voidBooking}><Ban className="h-4 w-4 mr-2" />Void</Button>}
      </PageHeader>

      {b.locked && <div className="mb-5 rounded-md border border-slate-700 bg-slate-900 px-4 py-2 text-sm text-slate-400 flex items-center gap-2" data-testid="locked-banner"><Lock className="h-4 w-4" />This record is locked after processing. Agent edits are disabled.</div>}

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="panel p-5" data-testid="service-panel">
          <div className="eyebrow mb-2">Service details</div>
          <Row k="Product" v={b.product_category} />
          <Row k="Supplier / Vendor" v={b.supplier_name} />
          <Row k="PNR / Supplier ref" v={b.supplier_ref} mono />
          <Row k="Lead traveler" v={b.traveler_name} />
          <Row k="Email" v={b.email} />
          <Row k="Phone" v={b.phone} />
          <Row k="Billing address" v={b.billing_address} />
          <Row k="Travel dates" v={b.travel_start ? `${fmtDateOnly(b.travel_start)} → ${b.travel_end ? fmtDateOnly(b.travel_end) : "one-way"}` : null} />
          {b.notes && <div className="mt-3 text-sm text-slate-300 whitespace-pre-wrap border-t border-slate-800 pt-3"><div className="eyebrow mb-1">Work notes</div>{b.notes}</div>}
        </div>

        <div className="panel p-5" data-testid="ledger-panel">
          <div className="eyebrow mb-2">Financial ledger</div>
          <Row k="Gross sale" v={<Money value={b.gross_amount} tone="gross" testId="detail-gross" />} />
          <Row k="Supplier net cost" v={<Money value={b.supplier_cost} tone="cost" testId="detail-cost" />} />
          <Row k="Agency MCO" v={<Money value={b.mco} tone="mco" testId="detail-mco" />} />
          <Row k="Billing type" v={<BillingBadge type={b.billing_type} />} />
          <Row k="Agency charges" v={<span className="font-mono font-bold">{fmtMoney(chargeTarget(b))}</span>} />
          <Row k="Target merchant" v={<MerchantBadge name={b.target_merchant_name} />} />
          <div className="text-[11px] text-slate-600 mt-2">{BILLING[b.billing_type]?.help}</div>
          {p && (
            <div className="mt-4 border-t border-slate-800 pt-3" data-testid="payment-result">
              <div className="eyebrow mb-2">Payment Desk result</div>
              <Row k="Outcome" v={<StatusBadge status={p.status} testId="payment-result-status" />} />
              <Row k="Processed merchant" v={<MerchantBadge name={p.processed_merchant_name} />} />
              <Row k="Charged amount" v={p.charged_amount != null ? <Money value={p.charged_amount} tone="gross" testId="payment-charged-amount" /> : null} />
              <Row k="Txn ID / Ref" v={p.txn_ref} mono />
              <Row k="Processed by" v={`${p.processed_by_name} · ${fmtDate(p.processed_at)}`} />
              <div className="text-sm text-slate-300 mt-2 whitespace-pre-wrap" data-testid="payment-notes">{p.notes}</div>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="panel p-5" data-testid="card-panel">
            <div className="flex items-center justify-between mb-2"><div className="eyebrow">Client card</div><Lock className="h-3.5 w-3.5 text-slate-600" /></div>
            <div className="rounded-lg border border-slate-700 bg-slate-950 p-4">
              <div className="text-xs text-slate-500">{b.card.card_type}</div>
              <div className="font-mono text-lg tracking-widest mt-2" data-testid="masked-card-number">{b.card.masked_number}</div>
              <div className="flex justify-between text-xs text-slate-400 mt-3"><span>{b.card.holder_name}</span><span className="font-mono">{b.card.expiry_masked}</span></div>
            </div>
            {user.role === "payment_team" ? (
              <Button data-testid="card-reveal-btn" onClick={() => setReveal(true)} variant="outline" className="w-full mt-3 border-amber-500/40 text-amber-300 hover:bg-amber-500/10"><Eye className="h-4 w-4 mr-2" />Reveal for processing</Button>
            ) : <div className="text-[11px] text-slate-600 mt-3">Masked. Full card data is accessible to the Payment Desk only.</div>}
          </div>
          <div className="panel p-5 text-xs text-slate-500 space-y-1" data-testid="meta-panel">
            <div>Created by <span className="text-slate-300">{b.agent_name}</span> · {fmtDate(b.created_at)}</div>
            <div>Last updated {fmtDate(b.updated_at)}</div>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mt-4">
        <DocumentsPanel booking={b} docs={documents} canEdit={canEdit} onChange={load} />
        <div className="panel p-5" data-testid="audit-panel">
          <div className="eyebrow mb-3">Audit trail</div>
          <ol className="space-y-3">
            {audit.map((a) => (
              <li key={a.id} className="flex gap-3 text-sm" data-testid={`audit-${a.action}`}>
                <div className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${a.action === "card_revealed" ? "bg-amber-400" : a.action.startsWith("payment_") ? "bg-emerald-400" : "bg-indigo-400"}`} />
                <div className="flex-1 min-w-0">
                  <div><span className="font-medium">{ACTION_LABEL[a.action] || a.action}</span><span className="text-slate-500"> · {a.actor_name} ({a.actor_role})</span></div>
                  {a.meta?.reason && <div className="text-xs text-amber-200/80">Reason: {a.meta.reason}</div>}
                  {a.meta?.txn_ref && <div className="text-xs text-slate-400 font-mono">{a.meta.merchant} · {a.meta.txn_ref} · {fmtMoney(a.meta.charged_amount)}</div>}
                  {a.meta?.to && <div className="text-xs text-slate-400">→ {a.meta.to}{a.meta.kind ? ` · ${a.meta.kind.replace(/_/g, " ")}` : ""}{a.meta.error ? ` · ${a.meta.error}` : ""}</div>}
                  {a.meta?.filename && <div className="text-xs text-slate-400">{a.meta.filename}</div>}
                  <div className="text-xs text-slate-600">{fmtDate(a.created_at)}</div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </div>

      <CardRevealModal booking={b} open={reveal} onOpenChange={setReveal} onRevealed={load} />
      <ProcessChargeDialog booking={b} open={proc} onOpenChange={setProc} onDone={load} />
    </div>
  );
}
